import { AppAction, Application } from '../../apis/eols-app-link/eols-app-link.constants';
import { AssignmentDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { RoutePath } from '../../components/app/app.constants';
import { CatalogPageTitleClickProps,
  GetAssignmentInfoProps,
  RecContentItemTitleHandlerProps,
  RecContentItemTitleHandlerPropsClone } from '../../models/assignment-types-shared.models';
import { getOsmosisTokenAndConfigPromise } from '../../pages/catalog/assignment-type-catalog-shared.utilities';
import { getSherpathLessonAssignmentInfo } from '../../pages/sherpath-lesson-editor/sherpath-lesson.utilities';
import { addSearchParams } from '../../utilities/app.utilities';
import { getCatalogItemFromSyllabusItem, getSherpathLesson, getSherpathLessonClone } from '../../utilities/assignment-types-shared.utilities';
import { stripNilProps } from '../../utilities/common.utilities';
import { ApplicationNavigateHandlerMapProps,
  AssignmentTypeStrategy,
  CoursePlanClickSettings,
  SherEvolAssignmentCreateProps } from '../model';
import { getExternalEntityCommon,
  navigateExternal,
  navigateToPerformanceReport,
  redirectInternal,
  sherEvolPerformanceViewRedirect,
  sherpathLessonTitleClick,
  sortCatalogForSequenceTypes } from '../utils/common.utils';

export const sherpathLesson: AssignmentTypeStrategy = {
  coursePlanTitleClick: sherpathLessonTitleClick,
  coursePlanClickSettings: (props: CoursePlanClickSettings) => {
    const { assignment, sharedAppLinkParams, defArgs, isDisableLinkToAMA, sharedSearchParams } = props;
    const { navigateToApp, redirect } = defArgs.props;
    if (isDisableLinkToAMA) {
      redirect(addSearchParams(RoutePath.SHERPATH_LESSON_EDITOR, sharedSearchParams));
    } else {
      const action = assignment ? AppAction.ASSIGNMENT_EDIT : AppAction.ASSIGNMENT_CREATE;
      navigateToApp({
        app: Application.ASSIGNMENT_MANAGEMENT,
        includeLinkHash: true,
        action,
        body: sharedAppLinkParams,
      });
    }
  },
  coursePlanPerformanceReportClick: navigateToPerformanceReport,
  catalogPageTitleClick: (props: CatalogPageTitleClickProps) => {
    const { defArgs, contentItem, isOsmosisVideosEnabled } = props;
    const { fetchOsmosisTokenAction, catalog, navigateToApp } = defArgs.props;
    const promise = getOsmosisTokenAndConfigPromise({
      contentItem,
      catalog,
      fetchOsmosisTokenAction,
      isOsmosisVideosEnabled
    });

    promise.then((response) => {
      const osmosisToken = response ? response.osmosisToken.authToken : null;
      const osmosisIncludesConfigs = response ? response.osmosisIncludesConfigs : null;

      navigateToApp({
        app: Application.SHERPATH,
        action: AppAction.ASSIGNMENT_PREVIEW,
        body: stripNilProps({
          catalogItem: contentItem,
          ref: RoutePath.CATALOG,
          osmosisToken,
          osmosisIncludesConfigs
        })
      });
    });
  },
  navigateToAppForAssignmentPerformanceHandlerMap: () => Application.SHER_EVOL,
  navigateToAppHandlerMap: () => Application.SHERPATH,
  assignmentOverviewTitleClick: (props: ApplicationNavigateHandlerMapProps) => navigateExternal(props),
  assignmentOverviewPerformanceReportClick: (props: ApplicationNavigateHandlerMapProps) => {
    if (props.isDifferentCourse) {
      navigateExternal(props);
    } else {
      redirectInternal(props);
    }
  },
  assignmentInfoHandler: (props: GetAssignmentInfoProps): Promise<AssignmentDto> => {
    const {
      newAssignment,
      syllabusItem,
      catalog,
      primaryTaxonomies,
      fetchLessonTopicsAction,
      evolveProducts
    } = props;
    const contentItem = getCatalogItemFromSyllabusItem(catalog, syllabusItem);
    return fetchLessonTopicsAction(contentItem.attributes.contentId).then((lessonTopics) => {
      const assignmentInfo = getSherpathLessonAssignmentInfo(catalog, contentItem, primaryTaxonomies, newAssignment, lessonTopics, evolveProducts);

      assignmentInfo.assignmentGoals[0].goal = newAssignment.gradePoints;
      return {
        ...newAssignment,
        ...assignmentInfo,
        title: newAssignment.title || assignmentInfo.title
      };
    });
  },
  getExternalEntity: getExternalEntityCommon,
  getExternalEntityClone: getExternalEntityCommon,
  sortCatalogItemsWithoutTaxonomy: sortCatalogForSequenceTypes,
  sortCatalogItemsWithoutTaxonomyClone: sortCatalogForSequenceTypes,
  recContentItemTitleHandler: (props: RecContentItemTitleHandlerProps) => {
    const lesson = getSherpathLesson(props.contentItem, props.catalog);
    if (lesson) {
      return lesson._parsedData.title;
    }
    return null;
  },
  recContentItemTitleHandlerClone: (props: RecContentItemTitleHandlerPropsClone) => {
    const lesson = getSherpathLessonClone(props.contentItem, props.externalEntitiesMap);
    if (lesson) {
      return lesson._parsedData.title;
    }
    return null;
  },
  sherEvolAssignmentCreate: (props: SherEvolAssignmentCreateProps) => {
    const { sharedSearchParams, redirect } = props;
    redirect(addSearchParams(RoutePath.SHERPATH_LESSON_EDITOR, sharedSearchParams));
  },
  sherEvolAssignmentPerformanceView: sherEvolPerformanceViewRedirect
};
